import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import oauthApi from '../../../services/oauthApi';
import { OAuthProviderType } from '../../../data/enums';

const EMPTY = { scopes: null, connected: false, needsReconnect: false };
const infoCache = new Map();

// Company-level Square connect info: required scope list (api-sourced) +
// proactive reconnect signal. DB-only endpoint; errors — including 404 from
// an api that predates it — degrade to EMPTY so old-api behavior is exactly
// today's (no banner, embedded scope fallback).
const useSquareConnectInfo = () => {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const companyId = globalCompany?.id;
  const [info, setInfo] = useState(() =>
    companyId && infoCache.has(companyId) ? infoCache.get(companyId) : EMPTY
  );
  const [checked, setChecked] = useState(
    () => !!(companyId && infoCache.has(companyId))
  );

  useEffect(() => {
    if (!companyId) {
      setInfo(EMPTY);
      setChecked(false);
      return undefined;
    }
    if (infoCache.has(companyId)) {
      setInfo(infoCache.get(companyId));
      setChecked(true);
      return undefined;
    }
    let cancelled = false;
    setInfo(EMPTY);
    setChecked(false);
    (async () => {
      try {
        const res = await oauthApi.squareConnectInfo({
          CompanyId: companyId,
          Type: OAuthProviderType.Square,
        });
        const d = res?.data?.data;
        const value = d
          ? {
              scopes: d.scopes || null,
              connected: !!d.connected,
              needsReconnect: !!d.needsreconnect,
            }
          : EMPTY;
        if (!cancelled) {
          infoCache.set(companyId, value);
          setInfo(value);
        }
      } catch (e) {
        if (!cancelled) setInfo(EMPTY);
      } finally {
        if (!cancelled) setChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  return { ...info, checked };
};

export default useSquareConnectInfo;
